import useSWR, { useSWRConfig } from 'swr'
import useSWRMutation from 'swr/mutation'
import { auditsApi, type AuditsApiError } from '../api/index.ts'
import type { ReviewBudget, ReviewRow } from '../types.ts'
import { auditKeys } from './audits.keys.ts'

export function useReviewQueue(auditId: string | undefined) {
  const { data, error, isLoading, mutate } = useSWR<
    { items: ReviewRow[]; budget: ReviewBudget },
    AuditsApiError
  >(auditId ? auditKeys.review(auditId) : null, async () => {
    const result = await auditsApi.reviewQueue(auditId!)
    if (!result.success) throw result.error
    return result.data
  })
  return {
    items: data?.items ?? [],
    budget: data?.budget ?? null,
    error: error ?? null,
    isLoading,
    refresh: mutate,
  }
}

/** The same facade method the MCP `review_queue.resolve` calls — one write path, one journal. */
export function useDecide(auditId: string) {
  const { mutate } = useSWRConfig()
  return useSWRMutation(
    auditKeys.review(auditId),
    async (
      _key,
      { arg }: { arg: { queueId: string; decision: 'accept' | 'reject'; reason: string } },
    ) => {
      const result = await auditsApi.decide(auditId, arg.queueId, arg)
      if (!result.success) throw result.error
      await Promise.all([
        mutate(auditKeys.review(auditId)),
        mutate(auditKeys.reviews(auditId)),
        mutate(auditKeys.detail(auditId)),
      ])
      return result.data
    },
  )
}
